import React, { useEffect, useState, KeyboardEvent, MouseEvent } from 'react'
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue
} from 'react-hook-form'
import { useTranslation } from '@contexts/translation'
import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Theme
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import makeStyles from '@mui/styles/makeStyles'
import { onSearchCompany } from '@api/contacts'

// Define interface for company suggestion returned from API
interface CompanySuggestion {
  nom_entreprise: string
  siren: string
  domaine_activite?: string
  capital?: string
  siege?: {
    adresse_ligne_1?: string
    code_postal?: string
    ville?: string
  }
  forme_juridique?: string
  [key: string]: any
}

interface SearchCompanyResponse {
  status: number
  data: {
    result: {
      resultats_siren: CompanySuggestion[]
    }
  }
}

interface CompanySearchPayload {
  company_name: string
  siren?: string
}

// Component props interface
interface SirenAutocompleteProps {
  /** The current SIREN value */
  siren: string
  /** React Hook Form control object */
  control: Control<any>
  /** React Hook Form errors object */
  errors?: FieldErrors
  /** React Hook Form setValue function */
  setValue: UseFormSetValue<any>
}

const useStyles = makeStyles((theme: Theme) => ({
  suggestionList: {
    borderRadius: '8px',
    border: `1px solid #919eab52`,
    marginTop: 8,
    padding: 5,
    listStyle: 'none',
    '& li:last-child': {
      borderBottom: 'none !important'
    }
  },
  suggestionItem: {
    borderBottom: `1px solid #919eab52`,
    cursor: 'pointer',
    padding: 5,
    '&:hover': { backgroundColor: theme.palette.action.hover }
  }
}))

/**
 * SirenAutocomplete component
 * An autocomplete component for SIREN numbers with company data integration
 */
export default function SirenAutocomplete(
  props: SirenAutocompleteProps
): JSX.Element {
  const { siren, control, errors, setValue } = props
  const { dictionary } = useTranslation()
  const classes = useStyles()
  const [suggestions, setSuggestions] = useState<CompanySuggestion[] | null>(
    null
  )
  const [error, setError] = useState<any>()

  useEffect(() => {
    if (errors && errors.siren) {
      setError(errors.siren)
    }
  }, [errors])

  const handleClickSearch = async () => {
    try {
      const response = (await onSearchCompany({
        company_name: '', // Providing empty string for company_name as it's required by the API
        siren
      })) as SearchCompanyResponse

      if (response) {
        if (response.status === 200) {
          setSuggestions(response.data.result.resultats_siren)
        }
      }
    } catch (error) {
      /* console.error('Error searching company by SIREN:', error) */
    }
  }

  const clearSuggestion = () => setSuggestions(null)

  const handleSelect =
    ({
      nom_entreprise,
      siren,
      domaine_activite,
      capital,
      siege,
      forme_juridique
    }: CompanySuggestion) =>
    () => {
      setValue('company_name', nom_entreprise)
      setValue('siren', siren)
      setValue('activity_type', domaine_activite || '')

      if (forme_juridique) {
        if (forme_juridique === 'Entrepreneur individuel') {
          setValue('entity_type', 'Indi')
        }
        if (forme_juridique === 'SARL, société à responsabilité limitée') {
          setValue('entity_type', 'SARL')
        }
        if (forme_juridique === "SA à conseil d'administration (s.a.i.)") {
          setValue('entity_type', 'SA')
        }
        if (forme_juridique === 'SAS, société par actions simplifiée') {
          setValue('entity_type', 'SAS')
        }
        if (forme_juridique === 'Société en nom collectif') {
          setValue('entity_type', 'SNC')
        }
      }

      if (capital) setValue('capital', capital)

      if (siege) {
        const address = `${siege.adresse_ligne_1 || ''} ${
          siege.code_postal || ''
        } ${siege.ville || ''}`.trim()
        setValue('headquarters_address', address)
        setValue('address', address)
      }

      clearSuggestion()
    }

  const renderSuggestions = () =>
    suggestions?.map(suggestion => {
      const { nom_entreprise, siren } = suggestion

      return (
        <li
          className={classes.suggestionItem}
          key={siren}
          onClick={handleSelect(suggestion)}
          onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              event.stopPropagation()
              handleSelect(suggestion)()
            }
          }}
          tabIndex={0}
          role="option"
        >
          <strong>{nom_entreprise}</strong> <small>{siren}</small>
        </li>
      )
    })

  return (
    <Grid item xs={12}>
      <Typography variant="caption">
        {dictionary.siren?.toString() || 'SIREN'}
      </Typography>
      <Controller
        control={control}
        name="siren"
        defaultValue=""
        render={({ field }) => (
          <>
            <TextField
              {...field}
              fullWidth
              variant="filled"
              error={Boolean(errors?.siren)}
              helperText={errors?.siren?.message?.toString()}
              InputProps={{
                endAdornment: (siren ?? '').length >= 6 && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickSearch}
                      onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                      data-testid="IconButton-35da6bfa-c9b5-4284-8819-6805758e34bd"
                      size="small"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                style: {
                  height: '44px',
                  paddingBottom: '15px',
                  overflow: 'hidden'
                }
              }}
            />

            {suggestions && suggestions.length > 0 && (
              <ul className={classes.suggestionList} role="listbox">
                {renderSuggestions()}
              </ul>
            )}

            {error && !errors?.siren?.message && (
              <div
                style={{
                  color: '#EE0701',
                  marginTop: 4,
                  marginLeft: 8,
                  marginBottom: 0,
                  fontSize: 12
                }}
              >
                {error.message}
              </div>
            )}
          </>
        )}
      />
    </Grid>
  )
}
